/**
 * `<MapOptionalFeatures/>` — B6.3, the per-team Map component toggles.
 *
 * Issue #754 / #762 (Phase 2). The lead picks which optional Map features
 * their team uses. Each is a plain on/off, backed by the SAME per-team
 * component mechanism the scheduling side uses:
 *
 *   - `useTeamFeatures(teamId)` reads `GET /api/teams/{id}/features` (one
 *     entry per registry component) — we filter it to the seven `task_*`
 *     Map keys (`taskFeatureKeys.ts`).
 *   - `useToggleComponent(teamId)` PATCHes `…/features/{key}` — we reuse it
 *     verbatim, including its dependency-cascade conflict surfacing.
 *
 * Faithful to `B6-3-optional-features.svg`: a single white card titled
 * "Map features", a divider, one row per feature (bold label · muted blurb ·
 * a teal pill toggle on the right), and a closing italic hint
 * "Turn on only what your team needs."
 *
 * Brand firewall (§A9, banlist): factual copy, **no tier/plan language** —
 * these are per-component toggles, never bundles. Lead-only: the parent
 * (`MapSettingsHome`) only opens this for a lead, and we also gate the
 * toggles read-only when `canManage` is false, so a non-lead never sees a
 * control the API would reject.
 *
 * Isolation: depends only on the generic features hook + UI primitives; no
 * scheduler imports.
 *
 * Editorial revamp (#1043, Wave 4, B6.3): theme-aware via `useMapTheme()`. The
 * `task_editorial_ui` flag stays OFF for every team (default LEGACY) until the
 * whole revamp lands, so:
 *   - LEGACY (flag off): byte-identical to the shipped soft-mint card — the
 *     shared `<Toggle>` pill on each row, the teal "Map features" heading.
 *   - EDITORIAL (flag on): the wireframe `V_B63` — a mono `MAP FEATURES` label
 *     over a serif "No tiers. Just toggles." heading; one row per feature
 *     (label · sub · an editorial switch: track `c.ink` when on / `c.bgRecess`
 *     when off, a cream knob); the italic "four-person audit vs seventy-person
 *     launch" footer.
 * Brand firewall holds in BOTH modes: "No tiers. Just toggles." — no
 * Free/Pro/Enterprise tier language, per-component toggles only.
 * RE-SKIN, not rebuild: every `data-testid`, copy, and behaviour is preserved.
 */

import { useMemo, useState } from "react";

import { ApiError } from "../../api/client";
import {
  type TeamFeatureItem,
  useTeamFeatures,
  useToggleComponent,
} from "../../api/useTeamFeature";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ErrorState,
  Skeleton,
  Toggle,
} from "../../components/ui";
import { useMapTheme } from "../theme/MapThemeContext";
import type { MapTheme } from "../theme/tokens";
import { TASK_FEATURE_KEYS, TASK_FEATURE_SPECS } from "./taskFeatureKeys";

type CascadeKind = "missing_dependencies" | "active_dependents";

interface CascadeConflict {
  kind: CascadeKind;
  componentKey: string;
  enabled: boolean;
  blockers: string[];
}

/**
 * Extract a dependency-cascade conflict envelope from a toggle `ApiError`.
 * The PATCH endpoint returns `{error: {detail: {kind, component_key,
 * blockers}}}` for 422 (enable, deps unmet) / 409 (disable, dependents
 * active). Mirrors the scheduling `<ComponentsPage>` parser so the two
 * surfaces handle the cascade identically. Returns `null` for any other
 * error shape, leaving the toggle untouched.
 */
function parseConflict(
  error: unknown,
  intent: { componentKey: string; enabled: boolean },
): CascadeConflict | null {
  if (!(error instanceof ApiError)) return null;
  if (error.status !== 422 && error.status !== 409) return null;
  const body = error.body as { error?: { detail?: unknown } } | null;
  const detail = body?.error?.detail;
  if (!detail || typeof detail !== "object") return null;
  const d = detail as { kind?: unknown; component_key?: unknown; blockers?: unknown };
  const kind = d.kind === "missing_dependencies" || d.kind === "active_dependents" ? d.kind : null;
  if (!kind) return null;
  const blockers = Array.isArray(d.blockers)
    ? d.blockers.filter((b): b is string => typeof b === "string")
    : [];
  return { kind, componentKey: intent.componentKey, enabled: intent.enabled, blockers };
}

export interface MapOptionalFeaturesProps {
  teamId: string | undefined;
  /** When false, the toggles render but are read-only (non-lead safety). */
  canManage: boolean;
}

export function MapOptionalFeatures({ teamId, canManage }: MapOptionalFeaturesProps) {
  const featuresQuery = useTeamFeatures(teamId);
  const toggle = useToggleComponent(teamId);
  const [conflict, setConflict] = useState<CascadeConflict | null>(null);
  const theme = useMapTheme();
  const c = theme.palette;
  const editorial = theme.mode === "editorial";

  // Filter the full registry response down to the seven Map keys and order
  // them as the gallery does. `enabled` comes straight from the server.
  const byKey = useMemo(() => {
    const map = new Map<string, TeamFeatureItem>();
    for (const f of featuresQuery.data?.features ?? []) {
      if (TASK_FEATURE_KEYS.has(f.key)) map.set(f.key, f);
    }
    return map;
  }, [featuresQuery.data]);

  // Every registry component by key, so a "Requires" line can name a
  // dependency that is not a Map feature (for example Telegram).
  const nameByKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const f of featuresQuery.data?.features ?? []) map.set(f.key, f.name);
    return map;
  }, [featuresQuery.data]);

  if (featuresQuery.isPending) {
    return (
      <div className="flex flex-col gap-3" data-testid="map-features-loading">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (featuresQuery.isError) {
    return (
      <ErrorState
        title="Couldn’t load Map features"
        message="Refresh the page; if it keeps happening, check your connection and try again."
      />
    );
  }

  const handleToggle = (componentKey: string, nextEnabled: boolean) => {
    if (!canManage) return;
    toggle.mutate(
      { componentKey, enabled: nextEnabled, cascade: false },
      {
        onError: (err) => {
          const c = parseConflict(err, { componentKey, enabled: nextEnabled });
          if (c) setConflict(c);
        },
      },
    );
  };

  const handleConfirmCascade = () => {
    if (!conflict) return;
    toggle.mutate(
      { componentKey: conflict.componentKey, enabled: conflict.enabled, cascade: true },
      {
        onSuccess: () => setConflict(null),
        onError: () => setConflict(null),
      },
    );
  };

  return (
    <div
      data-testid="map-optional-features"
      className={editorial ? "px-6 pb-5 pt-6" : undefined}
      style={{ fontFamily: editorial ? theme.fonts.sans : undefined }}
    >
      {/* Editorial masthead (`V_B63`): a mono MAP FEATURES label over the serif
          "No tiers. Just toggles." heading — the brand firewall made the title.
          Legacy keeps the in-card "Map features" heading only. */}
      {editorial && (
        <div className="mb-3 pr-8">
          <span
            className="text-[10px] uppercase"
            style={{ fontFamily: theme.fonts.mono, color: c.muted, letterSpacing: "0.1em" }}
          >
            Map features
          </span>
          <h3
            data-testid="features-heading"
            className="mt-1 text-[22px] italic leading-tight"
            style={{ fontFamily: theme.fonts.serif, fontWeight: 400, color: c.ink }}
          >
            No tiers. Just toggles.
          </h3>
        </div>
      )}

      <div
        className="overflow-hidden bg-white"
        style={
          editorial
            ? {
                border: `1px solid ${c.rule}`,
                borderRadius: theme.radii.lg,
                backgroundColor: c.cardBg,
              }
            : {
                borderRadius: 12,
                border: "1px solid #CFE0D8",
                boxShadow: "0 2px 8px rgba(31,122,110,0.12)",
              }
        }
      >
        {/* Legacy: the in-card "Map features" heading. Editorial: the masthead
            above carries it, so the card opens straight into the rows. */}
        {!editorial && (
          <div className="px-6 pt-5">
            <h3 className="text-sm font-bold" style={{ color: "#0C2A2A" }}>
              Map features
            </h3>
          </div>
        )}
        <div
          className={editorial ? "" : "mt-3 border-t"}
          style={{ borderColor: editorial ? undefined : "#F3F4F6" }}
        >
          {TASK_FEATURE_SPECS.map((spec) => {
            const item = byKey.get(spec.key);
            // A key the server didn't return (e.g. a not-yet-seeded env) is
            // simply not shown rather than rendered in an unknown state.
            if (!item) return null;
            const depNames = item.depends_on.map((k) => nameByKey.get(k) ?? k).filter(Boolean);
            return (
              <div
                key={spec.key}
                data-testid={`map-feature-row-${spec.key}`}
                className="flex items-center justify-between gap-4 border-t px-6 py-3 first:border-t-0"
                style={{ borderColor: editorial ? c.rule : "#F3F4F6" }}
              >
                <div className="min-w-0">
                  <span
                    className={editorial ? "text-[13.5px] font-medium" : "text-xs font-semibold"}
                    style={{
                      color: editorial ? c.ink : item.enabled ? "#0C2A2A" : "#9CA3AF",
                    }}
                  >
                    {spec.label}
                  </span>
                  {editorial ? (
                    <span className="ml-3 text-xs" style={{ color: c.muted }}>
                      {spec.blurb}
                    </span>
                  ) : (
                    <span className="ml-3 text-xs" style={{ color: "#9CA3AF" }}>
                      {spec.blurb}
                    </span>
                  )}
                  {depNames.length > 0 ? (
                    <p
                      className="mt-0.5 text-[11px]"
                      style={{ color: editorial ? c.muted : "#9CA3AF" }}
                    >
                      Requires: {depNames.join(", ")}
                    </p>
                  ) : null}
                </div>
                {editorial ? (
                  <EditorialSwitch
                    theme={theme}
                    checked={item.enabled}
                    disabled={!canManage || toggle.isPending}
                    onCheckedChange={(next) => handleToggle(spec.key, next)}
                    label={`${item.enabled ? "Turn off" : "Turn on"} ${spec.label}`}
                    testId={`map-feature-toggle-${spec.key}`}
                  />
                ) : (
                  <Toggle
                    checked={item.enabled}
                    onCheckedChange={(next) => handleToggle(spec.key, next)}
                    disabled={!canManage || toggle.isPending}
                    aria-label={`${item.enabled ? "Turn off" : "Turn on"} ${spec.label}`}
                    data-testid={`map-feature-toggle-${spec.key}`}
                  />
                )}
              </div>
            );
          })}
        </div>
        <div className="border-t px-6 py-3" style={{ borderColor: editorial ? c.rule : "#F3F4F6" }}>
          <p className="text-[11px] italic" style={{ color: editorial ? c.muted : "#9CA3AF" }}>
            Turn on only what your team needs.
          </p>
        </div>
      </div>

      {/* Editorial footer (`V_B63`): the calm "different needs" line beneath the
          card. Legacy has no line outside the card, so it renders only in
          editorial mode — legacy stays byte-identical. */}
      {editorial && (
        <p
          data-testid="features-needs-note"
          className="mt-5 text-center text-[12px] italic"
          style={{ color: c.muted }}
        >
          A four-person audit team has different needs to a seventy-person launch.
        </p>
      )}

      <CascadeDialog
        conflict={conflict}
        byKey={byKey}
        isPending={toggle.isPending}
        onConfirm={handleConfirmCascade}
        onCancel={() => setConflict(null)}
      />
    </div>
  );
}

/**
 * The editorial switch (`V_B63`): a 38×22 pill — track `c.ink` when ON /
 * `c.bgRecess` when OFF — with an 18px cream knob that slides. Keeps the same
 * `role="switch"` + `aria-checked` + `aria-label` + `data-testid` semantics as
 * the shared `<Toggle>`, so the existing tests resolve it identically; only the
 * look forks. (We render a bespoke switch rather than the shared `<Toggle>` so
 * the editorial track/knob colours come from the Map theme tokens, not the
 * app-shell accent palette — the wireframe switch is ink-on-cream, not accent.)
 */
function EditorialSwitch({
  theme,
  checked,
  disabled,
  onCheckedChange,
  label,
  testId,
}: {
  theme: MapTheme;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: (next: boolean) => void;
  label: string;
  testId: string;
}) {
  const c = theme.palette;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      data-testid={testId}
      className="relative shrink-0 rounded-full border transition-colors disabled:cursor-not-allowed disabled:opacity-50"
      style={{
        width: 38,
        height: 22,
        backgroundColor: checked ? c.ink : c.bgRecess,
        borderColor: checked ? c.ink : c.rule2,
      }}
    >
      <span
        aria-hidden
        className="absolute rounded-full transition-all"
        style={{
          top: 1,
          left: checked ? 17 : 1,
          width: 18,
          height: 18,
          backgroundColor: c.canvas,
        }}
      />
    </button>
  );
}

interface CascadeDialogProps {
  conflict: CascadeConflict | null;
  byKey: Map<string, TeamFeatureItem>;
  isPending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Confirmation when toggling one Map feature would require turning others on
 * (its dependencies) or off (its dependents). The seven Map keys ship with
 * no inter-dependencies today, so this rarely fires — but the mechanism is
 * the shared one, so we surface the cascade exactly like the scheduling page
 * rather than swallowing it.
 */
function CascadeDialog({ conflict, byKey, isPending, onConfirm, onCancel }: CascadeDialogProps) {
  const targetName = conflict
    ? (byKey.get(conflict.componentKey)?.name ?? conflict.componentKey)
    : "";
  const blockerNames = conflict ? conflict.blockers.map((k) => byKey.get(k)?.name ?? k) : [];
  const plural = blockerNames.length !== 1;

  const title =
    conflict?.kind === "missing_dependencies"
      ? `Turn on what ${targetName} needs?`
      : `Also turn off ${blockerNames.length} feature${plural ? "s" : ""} that ${
          plural ? "depend" : "depends"
        } on ${targetName}?`;

  const description =
    conflict?.kind === "missing_dependencies"
      ? `${targetName} needs ${plural ? "these features" : "this feature"} on first. Turn ${
          plural ? "them" : "it"
        } on along with ${targetName}?`
      : `${
          plural ? "These features" : "This feature"
        } depend on ${targetName} and will stop working if it’s off. Turn ${
          plural ? "them" : "it"
        } off too?`;

  return (
    <Dialog
      open={conflict !== null}
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
    >
      <DialogContent data-testid="map-feature-cascade-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {blockerNames.length > 0 ? (
          <ul className="flex flex-col gap-1 rounded-md border border-border bg-bg-subtle px-3 py-2">
            {blockerNames.map((name) => (
              <li key={name} className="text-sm text-fg">
                • {name}
              </li>
            ))}
          </ul>
        ) : null}
        <DialogFooter>
          <Button variant="secondary" onClick={onCancel} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isPending}
            data-testid="map-feature-cascade-confirm"
          >
            {conflict?.kind === "missing_dependencies" ? "Turn them all on" : "Turn them all off"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
